import axios from "../../api/axiosInstance";
import toast from "react-hot-toast";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { Link, useNavigate } from "react-router-dom";
import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import FilterPillBar from "./FilterPillBar";
import { FiMenu, FiX } from "react-icons/fi";
import BlockProfilesListPage from "./BlockProfilesListPage";

const ProfileSettingsPage = () => {
  const [activeTab, setActiveTab] = useState("editEmail");
  const [email, setEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [deleteReason, setDeleteReason] = useState("");
  const navigate = useNavigate();
  const [blockedProfiles, setBlockedProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilters, setActiveFilters] = useState({});
  const [menuOpenId, setMenuOpenId] = useState(null);
  const calculateAge = (dob) => dayjs().diff(dayjs(dob), "year");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const fetchBlockedProfiles = async () => {
    try {
      setLoading(true);
      const res = await axios.get("/api/block-profile-view/", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });
      setBlockedProfiles(res.data);
    } catch (error) {
      console.error(error.response?.data || error.message);
      toast.error("Failed to fetch blocked profiles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlockedProfiles();
  }, []);

  const handleUnblockProfile = (blockedId) => {
    confirmAlert({
      title: "Confirm Unblock",
      message: "Are you sure you want to unblock this profile?",
      buttons: [
        {
          label: "Yes, Unblock",
          onClick: async () => {
            try {
              await axios.post(
                "/api/unblock-profile/",
                { blocked_id: blockedId },
                {
                  headers: {
                    Authorization: `Bearer ${localStorage.getItem(
                      "accessToken"
                    )}`,
                  },
                }
              );
              toast.success("Profile unblocked successfully.");
              setBlockedProfiles((prev) =>
                prev.filter((profile) => profile.user.id !== blockedId)
              );
            } catch (error) {
              console.error(error.response?.data || error.message);
              toast.error("Failed to unblock profile.");
            }
          },
        },
        { label: "Cancel" },
      ],
    });
  };

  const handleEmailSave = async () => {
    try {
      const res = await axios.post("/api/accounts/request-email-update/", {
        email,
      });
      toast.success("OTP sent to your new email. Please verify.");
      localStorage.setItem("pendingUserEmail", email);
      navigate("/verify-otp");
      // reuse your existing OTP verification page
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Failed to send OTP. Please try again.");
      }
    }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match.");
      return;
    }
    try {
      await axios.post("/api/change-password/", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success("Password changed successfully.");
      navigate("/login");
    } catch (error) {
      toast.error("Failed to change password.");
    }
  };

  const handleDeactivateProfile = async () => {
    confirmAlert({
      title: "Confirm Deactivation",
      message: "Are you sure you want to deactivate your profile?",
      buttons: [
        {
          label: "Yes",
          onClick: async () => {
            try {
              await axios.post("api/accounts/deactivate-profile/");
              toast.success("Profile deactivated successfully.");
            } catch {
              toast.error("Failed to deactivate profile.");
            }
          },
        },
        {
          label: "No",
        },
      ],
    });
  };

  const handleDeleteProfile = async () => {
    confirmAlert({
      title: "Confirm Deletion",
      message: "Are you sure you want to permanently delete your profile?",
      buttons: [
        {
          label: "Yes",
          onClick: async () => {
            try {
              await axios.post("/api/delete-profile/", {
                reason: deleteReason,
              });
              toast.success("Profile deleted successfully.");
            } catch {
              toast.error("Failed to delete profile.");
            }
          },
        },
        {
          label: "No",
        },
      ],
    });
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = "/login";
  };

  return (
    <div className="max-w-6xl mx-auto p-4 flex flex-col md:flex-row gap-4">
      {/* Sidebar Toggle for Mobile */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="md:hidden flex items-center gap-2 text-orange-600 border border-orange-300 rounded px-3 py-2 w-max self-end"
      >
        {sidebarOpen ? <FiX /> : <FiMenu />}
        {sidebarOpen ? "Close Menu" : "Menu"}
      </button>

      {/* Sidebar */}
      <div
        className={`flex flex-col gap-1 md:w-64 bg-white border rounded p-2 shadow transition-transform duration-300 ${
          sidebarOpen ? "block" : "hidden md:block"
        }`}
      >
        {[
          { id: "editEmail", label: "Edit Email Address" },
          { id: "changePassword", label: "Change Password" },
          { id: "privacy", label: "Privacy" },
          { id: "deactivateProfile", label: "Deactivate Profile" },
          { id: "deleteProfile", label: "Delete Profile" },
          { id: "blockedProfiles", label: "Blocked Profiles" },
          { id: "logout", label: "Logout" },
        ].map((item) => (
          <button
            key={item.id}
            onClick={() => {
              setActiveTab(item.id);
              setSidebarOpen(true);
            }}
            className={`text-left px-4 py-2 rounded ${
              activeTab === item.id
                ? "bg-orange-100 text-orange-700 font-semibold"
                : "hover:bg-gray-100"
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 bg-white rounded shadow p-4 md:p-6">
        {activeTab === "editEmail" && (
          <>
            <h2 className="text-xl font-semibold mb-4">Edit Email Address</h2>
            <input
              type="email"
              placeholder="Enter your new email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="border p-2 w-full mb-4 rounded"
            />
            <div className="flex gap-2">
              <button
                onClick={handleEmailSave}
                className="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600"
              >
                Save
              </button>
              <button
                onClick={() => setEmail("")}
                className="bg-gray-300 text-gray-800 px-4 py-2 rounded hover:bg-gray-400"
              >
                Reset
              </button>
            </div>
          </>
        )}

        {activeTab === "changePassword" && (
          <>
            <h2 className="text-xl font-semibold mb-4">Change Password</h2>
            <input
              type="password"
              placeholder="Current Password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="border p-2 w-full mb-2 rounded"
            />
            <input
              type="password"
              placeholder="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="border p-2 w-full mb-2 rounded"
            />
            <input
              type="password"
              placeholder="Confirm New Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="border p-2 w-full mb-4 rounded"
            />
            <button
              onClick={handleChangePassword}
              className="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600"
            >
              Change Password
            </button>
          </>
        )}

        {activeTab === "deactivateProfile" && (
          <>
            <h2 className="text-xl font-semibold mb-4">Deactivate Profile</h2>
            <p className="mb-4 text-gray-600">
              Are you sure you want to temporarily deactivate your profile?
            </p>
            <button
              onClick={handleDeactivateProfile}
              className="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600"
            >
              Deactivate Profile
            </button>
          </>
        )}

        {activeTab === "deleteProfile" && (
          <>
            <h2 className="text-xl font-semibold mb-4">Delete Profile</h2>
            <p className="mb-2 text-gray-600">Select a reason for deletion:</p>
            <div className="flex flex-wrap gap-4 gap-y-2 mb-4">
              {["MARRIAGE FIXED", "MARRIED", "OTHER REASONS"].map((reason) => (
                <label key={reason} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="deleteReason"
                    value={reason}
                    checked={deleteReason === reason}
                    onChange={(e) => setDeleteReason(e.target.value)}
                  />
                  <span>{reason}</span>
                </label>
              ))}
            </div>

            <button
              onClick={handleDeleteProfile}
              className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
            >
              Delete Profile
            </button>
          </>
        )}

        {activeTab === "blockedProfiles" && (
          <>
            <h2 className="text-xl font-semibold mb-4">Blocked Profiles</h2>
            <BlockProfilesListPage />
          </>
        )}

        {activeTab === "logout" && (
          <>
            <h2 className="text-xl font-semibold mb-4">Logout</h2>
            <p className="mb-4 text-gray-600">
              Are you sure you want to logout?
            </p>
            <button
              onClick={handleLogout}
              className="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600"
            >
              Logout
            </button>
          </>
        )}

        {["privacy"].includes(activeTab) && (
          <>
            <h2 className="text-xl font-semibold mb-4">
              {activeTab.replace(/([A-Z])/g, " $1")}
            </h2>
            <p className="text-gray-600">This section is under development.</p>
          </>
        )}
      </div>
    </div>
  );
};

export default ProfileSettingsPage;
